import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TourService, TourView } from '../../services/tour.service';
import { Box, cardPosition, spotlightBox } from './tour-placement';

/**
 * The first-run tour: a scrim over the whole portal with a hole cut around one element, and a
 * card beside it. Next moves both to the following element.
 *
 * The scrim is the spotlight's own box-shadow, so the highlighted element is the real one, seen
 * through the hole, not a copy. A transparent layer underneath catches every click, so the page
 * cannot be used until the tour is finished or skipped.
 *
 * Placement runs outside Angular on every animation frame while the tour is open, and writes to
 * the DOM only when something moved. That is what lets the highlight follow an element that is
 * still settling — the project name loading into the switcher, the mobile drawer sliding in, a
 * resize — without a listener for each case or a change-detection pass per frame.
 */
@Component({
  selector: 'app-tour',
  standalone: true,
  imports: [CommonModule, A11yModule, MatButtonModule, MatIconModule],
  templateUrl: './tour.component.html',
  styleUrl: './tour.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TourComponent implements OnInit, OnDestroy {
  private readonly tourService = inject(TourService);
  private readonly zone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host: HTMLElement = inject(ElementRef).nativeElement;
  private readonly destroy$ = new Subject<void>();

  @ViewChild('spotlight') private spotlight?: ElementRef<HTMLElement>;
  @ViewChild('card') private card?: ElementRef<HTMLElement>;
  @ViewChild('primary', { read: ElementRef }) private primary?: ElementRef<HTMLButtonElement>;

  view: TourView | null = null;
  progressLabel = '';
  primaryLabel = 'Next';
  dotClasses: string[] = [];

  private frame = 0;
  /** What the last placement was computed from; an unchanged key means nothing to write. */
  private lastKey = '';
  /** Set on a new step, cleared once the frame loop has acted on it. */
  private pendingFocus = false;
  private pendingScroll = false;

  ngOnInit(): void {
    this.tourService.view$.pipe(takeUntil(this.destroy$)).subscribe(view => {
      const opening = !this.view && !!view;
      this.view = view;

      if (view) {
        this.progressLabel = `${view.index + 1} of ${view.total}`;
        this.primaryLabel = view.isLast ? 'Done' : 'Next';
        this.dotClasses = Array.from({ length: view.total }, (_, i) =>
          i === view.index ? 'tour-dot is-current' : i < view.index ? 'tour-dot is-done' : 'tour-dot'
        );
        this.pendingFocus = true;
        this.pendingScroll = true;
        this.lastKey = '';
        if (opening) this.startTracking();
      } else {
        this.stopTracking();
      }

      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.stopTracking();
    this.destroy$.next();
    this.destroy$.complete();
  }

  next(): void {
    this.tourService.next();
  }

  back(): void {
    this.tourService.back();
  }

  skip(): void {
    this.tourService.finish('skipped');
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') this.skip();
    else if (event.key === 'ArrowRight') this.next();
    else if (event.key === 'ArrowLeft') this.back();
    else return;
    event.preventDefault();
  }

  private startTracking(): void {
    this.host.classList.remove('is-placed');
    this.zone.runOutsideAngular(() => {
      const tick = () => {
        this.place();
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    });
  }

  private stopTracking(): void {
    cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  private place(): void {
    const spotlight = this.spotlight?.nativeElement;
    const card = this.card?.nativeElement;
    if (!this.view || !spotlight || !card) return;

    const element = document.querySelector<HTMLElement>(`[data-tour="${this.view.step.target}"]`);
    const shown = !!element && element.checkVisibility();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (this.pendingScroll && shown) {
      this.pendingScroll = false;
      element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    const target = shown ? onScreen(element.getBoundingClientRect(), viewportWidth, viewportHeight) : null;
    const cardWidth = card.offsetWidth;
    const cardHeight = card.offsetHeight;

    const key = `${target ? `${target.left},${target.top},${target.width},${target.height}` : '-'}|${cardWidth}x${cardHeight}|${viewportWidth}x${viewportHeight}`;
    if (key !== this.lastKey) {
      this.lastKey = key;

      const hole = spotlightBox(target, viewportWidth, viewportHeight);
      spotlight.style.transform = `translate(${hole.left}px, ${hole.top}px)`;
      spotlight.style.width = `${hole.width}px`;
      spotlight.style.height = `${hole.height}px`;
      spotlight.classList.toggle('is-empty', !target);

      const at = cardPosition(target && hole, cardWidth, cardHeight, viewportWidth, viewportHeight, this.view.step.placement);
      card.style.transform = `translate(${at.left}px, ${at.top}px)`;

      // The first placement lands without animation; from then on a move glides.
      if (!this.host.classList.contains('is-placed')) {
        requestAnimationFrame(() => this.host.classList.add('is-placed'));
      }
    }

    if (this.pendingFocus) {
      this.pendingFocus = false;
      this.primary?.nativeElement.focus({ preventScroll: true });
    }
  }
}

/** The element's box, or null when it has no size or lies wholly outside the viewport. */
function onScreen(rect: DOMRect, viewportWidth: number, viewportHeight: number): Box | null {
  const outside = rect.right <= 0 || rect.bottom <= 0 || rect.left >= viewportWidth || rect.top >= viewportHeight;
  if (rect.width === 0 || rect.height === 0 || outside) return null;
  return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
}
